/**
 * V203.9e - TASK 5: Feature Flags Service
 *
 * Centralized service for fetching and caching runtime feature flags from Supabase.
 * Enables emergency disable of features (like Android billing) without app updates.
 */

import { supabase } from '@/lib/supabase';

/** Fail-safe defaults when fetch fails or key is unknown. Risky flags default off. */
const SAFE_FLAG_DEFAULTS: Record<string, string> = {
  android_billing_enabled: 'false',
  web_billing_enabled: 'false',
  maintenance_mode: 'false',
  android_min_version: '1',
  announcement_message: '',
};

/**
 * Feature flags service with intelligent caching
 */
class FeatureFlags {
  private cache = new Map<string, string>();
  private lastFetch = 0;
  private readonly CACHE_TTL = 60000; // 60 seconds

  /**
   * Check if Android billing is enabled
   */
  async isAndroidBillingEnabled(): Promise<boolean> {
    const value = await this.getFlag('android_billing_enabled');
    return value === 'true';
  }

  /**
   * Check if web billing is enabled
   */
  async isWebBillingEnabled(): Promise<boolean> {
    const value = await this.getFlag('web_billing_enabled');
    return value === 'true';
  }

  /**
   * Check if app is in maintenance mode
   */
  async isMaintenanceMode(): Promise<boolean> {
    const value = await this.getFlag('maintenance_mode');
    return value === 'true';
  }

  /**
   * Get minimum required Android app version
   */
  async getAndroidMinVersion(): Promise<number> {
    const value = await this.getFlag('android_min_version');
    return parseInt(value, 10) || 1;
  }

  /**
   * Get announcement message
   */
  async getAnnouncementMessage(): Promise<string> {
    const value = await this.getFlag('announcement_message');
    return value || '';
  }

  /**
   * Get any feature flag by key
   */
  async getFlag(key: string, defaultValue?: string): Promise<string> {
    const safeDefault = defaultValue ?? SAFE_FLAG_DEFAULTS[key] ?? 'false';
    const now = Date.now();

    // Cache hit: Return cached value if fresh
    if (this.cache.has(key) && now - this.lastFetch < this.CACHE_TTL) {
      const cachedValue = this.cache.get(key)!;
      console.log(`🏁 Feature flag [${key}] from cache:`, cachedValue);
      return cachedValue;
    }

    // Cache miss: Fetch from database
    try {
      console.log(`🔍 Fetching feature flag [${key}] from database...`);

      const { data, error } = await supabase
        .from('app_config')
        .select('value')
        .eq('key', key)
        .single();

      if (error) throw error;
      if (!data) throw new Error(`Flag ${key} not found in database`);

      // Update cache
      this.cache.set(key, data.value);
      this.lastFetch = now;

      console.log(`✅ Feature flag [${key}] fetched:`, data.value);
      return data.value;
    } catch (error) {
      console.warn(`⚠️ Failed to fetch feature flag [${key}]:`, error);

      // Fail-safe: Use stale cache if available, else documented safe default
      if (this.cache.has(key)) {
        const staleValue = this.cache.get(key)!;
        console.warn(`⚠️ Using stale cached value for [${key}]:`, staleValue);
        return staleValue;
      }

      console.warn(`⚠️ Defaulting to "${safeDefault}" for [${key}] (fail-safe)`);
      return safeDefault;
    }
  }

  /**
   * Clear cache (useful for testing)
   */
  clearCache(): void {
    this.cache.clear();
    this.lastFetch = 0;
    console.log('🗑️ Feature flags cache cleared');
  }

  /**
   * Get cache statistics (for debugging)
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      lastFetch: this.lastFetch ? new Date(this.lastFetch).toISOString() : 'never',
      age: this.lastFetch ? Date.now() - this.lastFetch : 0,
      ttl: this.CACHE_TTL,
      isStale: Date.now() - this.lastFetch > this.CACHE_TTL,
      keys: Array.from(this.cache.keys()),
    };
  }

  /**
   * Preload all flags (call at app startup)
   */
  async preload(): Promise<void> {
    console.log('🔥 Preloading feature flags...');

    try {
      const { data, error } = await supabase
        .from('app_config')
        .select('key, value');

      if (error) throw error;

      if (data) {
        data.forEach(flag => {
          this.cache.set(flag.key, flag.value);
        });
        this.lastFetch = Date.now();
        console.log(`✅ Preloaded ${data.length} feature flags`);
      }
    } catch (error) {
      console.warn('⚠️ Failed to preload feature flags:', error);
    }
  }
}

// Export singleton instance
export const featureFlags = new FeatureFlags();

// Type definitions
export interface FeatureFlagConfig {
  android_billing_enabled: boolean;
  web_billing_enabled: boolean;
  maintenance_mode: boolean;
  android_min_version: number;
  announcement_message: string;
}

export type FeatureFlagKey = keyof FeatureFlagConfig;
